import type { MetricDashboardSource } from "@/metrics/catalog"

import { ANOMALY_DIRECTION } from "@/types/api/MetricBaseline"
import {
  CONVERSATION_SOURCE,
  type ConversationSource,
} from "@/types/conversationSource"

import type { WidgetExportData } from "../components/BaseWidget"
import type { GroupByType } from "./chart-types"
import type { DashboardConfig } from "./dashboardConfig"
import type { WidgetDateRange } from "./dateRange"
import type { MetadataFilter } from "./filters"

export interface DashboardMeta {
  id: string
  display_name: string
  description: string | null
  is_default: boolean
  is_favorite: boolean
  created_at: string
  last_updated_at: string
  organization_id: string
  config?: DashboardConfig | null
}

export interface DashboardWidget {
  id: string
  type: string
  title: string
  config?: DashboardWidgetConfig
  onConfigChange?: (newConfig: DashboardWidgetConfig) => void

  // Grid properties. grid_w is the width the user asked for; the other three
  // are the packed result written by the flow layout. See dashboardFlowLayout.
  grid_x?: number | null // Column position (0-47)
  grid_y?: number | null // Row position (0+)
  grid_w?: number | null // Requested width in columns
  grid_h?: number | null // Height in rows, derived from the widget type
}

export interface FilterType {
  metricIds?: string[]
  agentIds?: string[]
  agentMutationIds?: string[]
  personaIds?: string[]
  templateNames?: string[]
  tagFilters?: string[]
  testSetIds?: string[]
  testCaseIds?: string[]
  metadata?: MetadataFilter[]
}

export interface DrillDownRunRecord {
  simulation_output_id: string
  run_id: string
  occurred_at: string
  created_at?: string
  value: number | string
  metric_id: string
  agent_id?: string
  persona_id?: string
  template_name?: string
  test_set_id?: string
}

export interface DashboardWidgetConfig {
  title?: string
  description?: string
  text?: string
  metricId?: string
  metricIds?: string[]
  dataSource?: DataSource
  monitoring?: string
  aggregation?: AggregationType
  visualizationType?: VisualizationType
  metricOutputType?: MetricOutputType
  groupBy?: GroupByType
  groupByMetadataKey?: string
  splitBy?: GroupByType
  timeMode?: TimeMode
  filters?: FilterType
  metricFilter?: MetricFilter[]
  dateRange?: WidgetDateRange
  bucketInterval?: string
  stacked?: boolean
  grouped?: boolean
  showAsPercentage?: boolean
  showTargetZone?: boolean
  showBaselineBands?: boolean
  baselineId?: string
  baselineSigmaLevels?: number[]
  baselineDirection?: BaselineDirection
  baselineBandColor?: string
  xAxisLabel?: string
  yAxisLabel?: string
  customColorMap?: Record<string, string>
  customSeriesNames?: Record<string, string>
  hiddenSeries?: string[]
  format?: WidgetFormat
  precision?: number
  units?: string
  showCount?: boolean
  showRange?: boolean
  showStdDev?: boolean
  showBoxPlot?: boolean
  metric?: MetricDashboardSource | null
  metricManager?: string
  colorScheme?: string
}

export const DATA_SOURCE = {
  UPLOADED: CONVERSATION_SOURCE.UPLOADED,
  SIMULATED: CONVERSATION_SOURCE.SIMULATED,
  HUMAN_REVIEW: "human_review",
} as const

export type DataSource = ConversationSource | typeof DATA_SOURCE.HUMAN_REVIEW

export const METRIC_OUTPUT_TYPE = {
  STRING: "string",
  FLOAT: "float",
} as const

export type MetricOutputType =
  (typeof METRIC_OUTPUT_TYPE)[keyof typeof METRIC_OUTPUT_TYPE]

export const BASELINE_DIRECTION = {
  NONE: "NONE",
  BOTH: ANOMALY_DIRECTION.BOTH,
  ABOVE: ANOMALY_DIRECTION.ABOVE,
  BELOW: ANOMALY_DIRECTION.BELOW,
} as const

export type BaselineDirection =
  (typeof BASELINE_DIRECTION)[keyof typeof BASELINE_DIRECTION]

export const METRIC_FILTER_OPERATORS = [
  ">",
  "<",
  ">=",
  "<=",
  "=",
  "!=",
] as const

export type MetricFilterOperator = (typeof METRIC_FILTER_OPERATORS)[number]

export interface MetricFilter {
  metricId: string
  operator: MetricFilterOperator
  value: number | string
  metricOutputType: MetricOutputType
}

export const WIDGET_FORMAT = {
  NUMBER: "number",
  PERCENTAGE: "percentage",
  DURATION: "duration",
} as const

export type WidgetFormat = (typeof WIDGET_FORMAT)[keyof typeof WIDGET_FORMAT]

export const VISUALIZATION_TYPE = {
  LINE: "line",
  BAR: "bar",
  AREA: "area",
  STATISTIC: "statistic",
  PIE: "pie",
  HISTOGRAM: "histogram",
  TOP_LIST: "top-list",
  TEXT: "text",
} as const

export type VisualizationType =
  (typeof VISUALIZATION_TYPE)[keyof typeof VISUALIZATION_TYPE]

export const TIME_MODE = {
  OVER_TIME: "over_time",
  WHOLE_RANGE: "whole_range",
} as const

export type TimeMode = (typeof TIME_MODE)[keyof typeof TIME_MODE]

export const AGGREGATION_TYPE = {
  SUM: "sum",
  COUNT: "count",
  AVG: "avg",
  MAX: "max",
  MIN: "min",
  SUCCESS: "success",
  P50: "p50",
  P90: "p90",
  P95: "p95",
  P99: "p99",
} as const

// Aggregation type for metric-based widgets
export type AggregationType =
  (typeof AGGREGATION_TYPE)[keyof typeof AGGREGATION_TYPE]

/** Success rate or avg on string metrics (YES=1, NO=0) */
export const isBinaryAggregation = (
  aggregation: string | undefined,
  metricOutputType: MetricOutputType | undefined,
): boolean =>
  aggregation === AGGREGATION_TYPE.SUCCESS ||
  (aggregation === AGGREGATION_TYPE.AVG &&
    metricOutputType === METRIC_OUTPUT_TYPE.STRING)

export enum AggregationFieldType {
  CATEGORICAL = "categorical",
}

export enum AggregationField {
  AGENT = "agentId",
  PERSONA = "personaName",
}

// New aggregation configuration types for Task 2.0
export interface AggregationConfig {
  id: string
  field: AggregationField // e.g., "agentId", "personaId"
  label: string // e.g., "Agent", "Persona", "Test Set "
  type: AggregationFieldType // For now, all aggregations are categorical
}

// Widget-level filtering configuration
export interface WidgetFilterConfig {
  // Agent filtering
  agentIds?: string[]
  // Persona filtering
  personaIds?: string[]
  // Template filtering
  templateNames?: string[]
  // Tag filtering
  tagFilters?: string[]
  // Test Set filtering
  testSetIds?: string[]
  // Test Case filtering
  testCaseIds?: string[]
}

export interface WidgetTemplate {
  type: string
  title: string
  description: string
  category?: string
  icon: React.ReactNode
  configSchema?: Record<string, unknown>
  component?: React.ComponentType<Record<string, unknown>>
  disabled?: boolean
}

export interface GlobalDateRange {
  start: Date
  end: Date
}

export interface SelectedPointState {
  point: BucketClickData
  widgetId: string
  metricId: string
  isMonitoring: boolean
  dataSource?: DataSource
}

export interface DashboardWidgetProps {
  dashboardId: string
  widget: DashboardWidget
  onRemove: (id: string) => void
  onConfigure?: (id: string) => void
  onClone?: (id: string) => void
  onConfigChange?: (id: string, config: DashboardWidgetConfig) => void
  onWidthChange?: (id: string, columns: number) => void
  children?: React.ReactNode
}

export interface WidgetFillerContentProps {
  type: string
  widget: DashboardWidget
  onPointClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  onChartClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  selectedPointInChart?: BucketClickData | null
  onSplitKeysChange?: (splitKeys: string[]) => void
  onExportDataReady?: (data: WidgetExportData | null) => void
}

// Data structure for click events
export interface BucketClickData {
  timestamp: number
  category?: string
  aggregatedValue: number
  bucketCount: number
  timeRange: { start: Date; end: Date }
  metricId: string
  aggregationType: string
  rawDataPoints: Record<string, unknown>[]
  color?: string // Color of the clicked bar/chart element
}
